import api from "./axios";
import { TechnicalNotesListParams, TechnicalNotesListResponse, TechnicalNote, TechnicalNoteCreateRequest, TechnicalNoteUpdateRequest, TechnicalNotesListManagementParams, SubscriptionsListManagementParams, SubscriptionsListResponse, SubscriptionUpdateRequest, SubscriptionCreateRequest, SubscriptionPlanCreateRequest, SubscriptionPlan, SubscriptionPlansListManagementParams, SubscriptionPlansListResponse, SubscriptionPlanUpdateRequest } from "../types";

export interface ModelMetadata {
  [key: string]: any;
}

/**
 * Configuration for a single model type
 * Used for creating new models with predefined templates
 */
export interface ModelConfig {
  provider: string;
  endpoint_url: string;
  api_key: string;
  api_version?: string;
  model_name: string;
  model_kwargs?: Record<string, any>;
  dimension?: number;
}

export interface Model {
  id: string;
  name: string;
  model: string; // GPT_OSS, AZURE_GPT_41, etc.
  model_metadata: ModelMetadata;
  created_at?: string;
  updated_at?: string;
  archived?: boolean;
}

export interface CreateModelRequest {
  name: string;
  type: string; // text-embedding-3-small, gpt-4.1, etc.
  model_metadata: ModelMetadata;
}

export interface UpdateModelRequest {
  name: string;
  model_metadata: ModelMetadata;
}

// Mapping from display names to enum values
export const LLM_MODEL_MAPPING: Record<string, string> = {
  "GPT OSS 120b": "GPT_OSS",
  "Azure OpenAI GPT 4.1": "AZURE_GPT_41",
  "Azure OpenAI GPT 4.1 Mini": "AZURE_GPT_41_MINI",
  "OpenAI GPT 4.1": "GPT_41",
  "OpenAI GPT 4.1 Mini": "GPT_41_MINI",
  "AgentIQ AI 1": "AGENTIQ_AI_1",
};

export const EMBEDDING_MODEL_MAPPING: Record<string, string> = {
  "Azure OpenAI Text Embedding 3 Small": "AZURE_TEXT",
  "BGE Embedding M3": "BGE",
  "AgentIQ AI Embedding 1": "AGENTIQ_AI_EMBEDDING_1",
};

// Global Models API
export const globalModelsApi = {
  // Create global LLM model
  createLLM: async (data: CreateModelRequest): Promise<Model> => {
    const response = await api.post("/models/llm-model", data);
    return response.data;
  },

  // Create global embedding model
  createEmbedding: async (data: CreateModelRequest): Promise<Model> => {
    const response = await api.post("/models/embedding-model", data);
    return response.data;
  },

  // Get all global LLM models
  getLLMModels: async (): Promise<Model[]> => {
    const response = await api.get("/models/llm-models");
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get all global embedding models
  getEmbeddingModels: async (): Promise<Model[]> => {
    const response = await api.get("/models/embedding-models");
    return Array.isArray(response.data) ? response.data : [];
  },

  // Update global LLM model
  updateLLM: async (modelId: string, data: UpdateModelRequest): Promise<Model> => {
    const response = await api.put(`/models/llm-model/${modelId}`, data);
    return response.data;
  },

  // Update global embedding model
  updateEmbedding: async (modelId: string, data: UpdateModelRequest): Promise<Model> => {
    const response = await api.put(`/models/embedding-model/${modelId}`, data);
    return response.data;
  },

  // Archive global model
  archive: async (modelId: string): Promise<void> => {
    await api.put(`/models/${modelId}/archive`, {});
  },
};

// Organization Models API
export const organizationModelsApi = {
  // Create organization LLM model
  createLLM: async (organizationId: string, data: CreateModelRequest): Promise<Model> => {
    const response = await api.post(`/organizations/${organizationId}/llm-model`, data);
    return response.data;
  },

  // Create organization embedding model
  createEmbedding: async (organizationId: string, data: CreateModelRequest): Promise<Model> => {
    const response = await api.post(`/organizations/${organizationId}/embedding-model`, data);
    return response.data;
  },

  // Get all organization LLM models
  getLLMModels: async (organizationId: string): Promise<Model[]> => {
    const response = await api.get(`/organizations/${organizationId}/llm-models`);
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get all organization embedding models
  getEmbeddingModels: async (organizationId: string): Promise<Model[]> => {
    const response = await api.get(`/organizations/${organizationId}/embedding-models`);
    return Array.isArray(response.data) ? response.data : [];
  },

  // Update organization LLM model
  updateLLM: async (organizationId: string, modelId: string, data: UpdateModelRequest): Promise<Model> => {
    const response = await api.put(`/organizations/${organizationId}/llm-model/${modelId}`, data);
    return response.data;
  },

  // Update organization embedding model
  updateEmbedding: async (organizationId: string, modelId: string, data: UpdateModelRequest): Promise<Model> => {
    const response = await api.put(`/organizations/${organizationId}/embedding-model/${modelId}`, data);
    return response.data;
  },

  // Archive organization model
  archive: async (organizationId: string, modelId: string): Promise<void> => {
    await api.put(`/organizations/${organizationId}/model/${modelId}/archive`, {});
  },
};

// Model Assignment APIs
export interface ModelAssignmentPayload {
  task_types: {
    DECOMPOSE: string | null;
    PLAN: string | null;
    EXECUTE: string | null;
    SYNTHESIZE: string | null;
    GENERATE_TITLE: string | null;
    WORKFLOW_RETRIEVE: string | null;
    RESOURCE_GENERATE: string | null;
    IMAGE_MODEL: string | null;
  };
  embedding_model_id: string | null;
}

// Available Models API
export interface AvailableModel {
  id: string;
  name: string;
}

export const availableModelsApi = {
  // Global available models
  global: {
    getLLMModels: async (): Promise<AvailableModel[]> => {
      const response = await api.get("/models/available-llm-models");
      return Array.isArray(response.data) ? response.data : [];
    },
    getEmbeddingModels: async (): Promise<AvailableModel[]> => {
      const response = await api.get("/models/available-embedding-models");
      return Array.isArray(response.data) ? response.data : [];
    },
  },
  
  // Organization available models
  organization: {
    getLLMModels: async (organizationId: string): Promise<AvailableModel[]> => {
      const response = await api.get(`/organizations/${organizationId}/available-llm-models`);
      return Array.isArray(response.data) ? response.data : [];
    },
    getEmbeddingModels: async (organizationId: string): Promise<AvailableModel[]> => {
      const response = await api.get(`/organizations/${organizationId}/available-embedding-models`);
      return Array.isArray(response.data) ? response.data : [];
    },
  },
};

export interface ModelAssignmentDetail {
  task_types: {
    DECOMPOSE: string | null;
    PLAN: string | null;
    EXECUTE: string | null;
    SYNTHESIZE: string | null;
    GENERATE_TITLE: string | null;
    WORKFLOW_RETRIEVE: string | null;
    RESOURCE_GENERATE: string | null;
    IMAGE_MODEL: string | null;
  };
  embedding_model_id: string | null;
}

export const modelAssignmentApi = {
  // Global model assignments
  global: {
    // Detail (preferred) - GET /api/models/model-assignment
    get: async (): Promise<ModelAssignmentDetail> => {
      const response = await api.get("/models/model-assignment");
      return response.data as ModelAssignmentDetail;
    },
    save: async (payload: ModelAssignmentPayload): Promise<void> => {
      await api.post("/models/model-assignment", payload);
    },
  },
  
  // Organization model assignments
  organization: {
    // Detail (preferred) - GET /api/organizations/{id}/model-assignment
    get: async (organizationId: string): Promise<ModelAssignmentDetail> => {
      const response = await api.get(`/organizations/${organizationId}/model-assignment`);
      return response.data as ModelAssignmentDetail;
    },
    save: async (organizationId: string, payload: ModelAssignmentPayload): Promise<void> => {
      await api.post(`/organizations/${organizationId}/model-assignment`, payload);
    },
  },
};

// API Key types and interfaces
export interface ApiKey {
  id: string;
  name: string;
  api_key: string;
  expire_date: string;
  permissions: string[];
  domains: string[];
  ip_addresses: string[];
  status: string;
  guest_user_id: string;
  workspace_id: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

export interface CreateApiKeyRequest {
  name: string;
  expire_date: string;
  permissions: string[];
  domains?: string[];
  ip_addresses?: string[];
}

export interface CreateApiKeyResponse {
  id: string;
  api_key: string;
}

export interface UpdateApiKeyRequest {
  name: string;
  expire_date: string;
  permissions: string[];
  domains: string[];
}

// API Keys API
export const apiKeysApi = {
  // Get all API keys for a specific workspace
  getAll: async (workspaceId: string): Promise<{ api_keys: ApiKey[] }> => {
    const response = await api.get(`/workspaces/${workspaceId}/api_keys`);
    return response.data;
  },

  // Create new API key
  create: async (workspaceId: string, data: CreateApiKeyRequest): Promise<CreateApiKeyResponse> => {
    const response = await api.post(`/workspaces/${workspaceId}/api_key`, data);
    return response.data;
  },

  // Update API key
  update: async (workspaceId: string, apiKeyId: string, data: UpdateApiKeyRequest): Promise<ApiKey> => {
    const response = await api.put(`/workspaces/${workspaceId}/api_key/${apiKeyId}`, data);
    return response.data;
  },
    
  // Delete API key
  delete: async (workspaceId: string, apiKeyId: string): Promise<void> => {
    await api.delete(`/workspaces/${workspaceId}/api_key/${apiKeyId}`);
  },
};

// Datasource API
export const datasourceApi = {
  // Get permissions for a workspace
  getPermissions: async (workspaceId: string): Promise<string[]> => {
    const response = await api.get(`/datasource/permissions`, {
      params: { workspace_id: workspaceId }
    });
    
    const data = response.data;
    
    // Handle object response with permissions property
    if (data && typeof data === 'object' && 'permissions' in data) {
      return Array.isArray(data.permissions) ? data.permissions : [];
    }
    
    // Handle direct array response
    if (Array.isArray(data)) {
      return data;
    }
    
    // Handle string response
    if (typeof data === 'string') {
      return data ? [data] : [];
    }
    
    // Fallback to empty array
    return [];
  },
};

// Technical Notes API
export const technicalNotesApi = {
  // Get list of technical notes with filtering and pagination
  getList: async (params: TechnicalNotesListParams): Promise<TechnicalNotesListResponse> => {
    const response = await api.get("/technical-notes", { params });
    return response.data;
  },

  // Get list of technical notes in management
  getListManagement: async (params: TechnicalNotesListManagementParams): Promise<TechnicalNotesListResponse> => {
    const response = await api.get("/technical-notes/search", { params });
    return response.data;
  },


  // Create new technical note
  create: async (data: TechnicalNoteCreateRequest): Promise<TechnicalNote> => {
    const response = await api.post("/technical-notes", data);
    return response.data;
  },

  // Update technical note
  update: async (id: string, data: TechnicalNoteUpdateRequest): Promise<TechnicalNote> => {
    const response = await api.put(`/technical-notes/${id}`, data);
    return response.data;
  },

  // Delete technical note
  delete: async (id: string): Promise<void> => {
    await api.delete(`/technical-notes/${id}`);
  },

  // Get pending technical notes count
  getPendingCount: async (params: {
    agent_id?: string | null;
    workspace_id?: string;
  }): Promise<{ count: number }> => {
    const response = await api.get("/technical-notes/pending/count", { params });
    return response.data;
  },
};

// Subscriptions API
export const subscriptionsApi = {
  // Get list of subscriptions with filtering and pagination
  getListManagement: async (params: SubscriptionsListManagementParams): Promise<SubscriptionsListResponse> => {
    const response = await api.get("/subscriptions/list", { params });
    return response.data;
  },

  // Create a new subscription
  create: async (data: SubscriptionCreateRequest): Promise<void> => {
    await api.post("/subscriptions/", data);
  },

  // Update subscription (name, description, and/or status)
  update: async (id: string, data: SubscriptionUpdateRequest): Promise<void> => {
    await api.put(`/subscriptions/${id}`, data);
  },

  // Activate a subscription plan
  activate: async (id: string): Promise<void> => {
    await api.put(`/subscriptions/${id}/activate`);
  },

  // Deactivate a subscription plan
  deactivate: async (id: string): Promise<void> => {
    await api.put(`/subscriptions/${id}/deactivate`);
  },

  // Check activation status for local deployment (no token required)
  getActivationStatus: async (): Promise<{ is_activated: boolean }> => {
    const response = await api.get("/subscriptions/activation-status");
    return response.data;
  },
};

// Subscription Plans API
export const subscriptionPlansApi = {
  // Get list of subscription plans with filtering and pagination
  getListManagement: async (params: SubscriptionPlansListManagementParams): Promise<SubscriptionPlansListResponse> => {
    const response = await api.get("/subscription-plans", { params });
    return response.data;
  },

  // Create a new subscription plan
  create: async (data: SubscriptionPlanCreateRequest): Promise<void> => {
    await api.post("/subscription-plans/", data);
  },

  // // Update subscription plan (name, description, and/or status)
  // update: async (id: string, data: SubscriptionPlanUpdateRequest): Promise<void> => {
  //   await api.put(`/subscription-plans/${id}`, data);
  // },

  // Activate a subscription plan
  activate: async (id: string): Promise<void> => {
    await api.put(`/subscription-plans/${id}/activate`);
  },

  // Deactivate a subscription plan
  deactivate: async (id: string): Promise<void> => {
    await api.put(`/subscription-plans/${id}/deactivate`);
  },
};